import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Truck, Store, Building2, Upload, FileText, X } from "lucide-react";
import { api, apiError, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCZK } from "@/lib/format";
import { splitAmount, MODES } from "@/lib/orders";
import { SupplierPicker } from "@/components/suppliers/SupplierPicker";
import { SupplierEditDialog } from "@/components/suppliers/SupplierEditDialog";
import { autoPill } from "@/components/shared/Pill";

const CLOSED = ["Dokončeno", "Fakturováno", "Záruka"];
const RECEIVERS = ["OWNER", "ASSISTANT", "MANAGER", "WORKER", "WAREHOUSE"];
const OK_FILE = /\.(pdf|jpe?g|png|webp|heic|heif)$/i;
const MODE_ICON = { site: Truck, pickup: Store, office: Building2 };
const OFFICE = "office";

const fromOrder = (o) => ({
  supplier_id: o.supplier_id || "", supplier_name: o.supplier_name || "", title: o.title || "", project_id: o.project_id || OFFICE, order_no: o.order_no || "",
  amount: o.amount_mode === "net" ? (o.amount_net ?? "") : (o.amount_gross ?? ""), amount_mode: o.amount_mode || "gross",
  vat_rate: o.vat_rate ?? 21, mode: o.delivery?.mode || "site", carrier: o.delivery?.carrier || "", branch: o.delivery?.branch || "",
  due_date: o.due_date || "", due_note: o.due_note || "", receiver_id: o.receiver_id || "", note: o.note || "", files: [],
});
const blank = (projectId) => ({ ...fromOrder({}), project_id: projectId || "" });

function Field({ label, req, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <Label className="text-[12px] font-bold uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-brand"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Nová objednávka / úprava (311). Z pasportu přijde se zakázkou a položkami (pasport_item_ids).
export function OrderDialog({ open, onClose, order, pasport, defaultProjectId, onSaved }) {
  const { user } = useAuth();
  const edit = !!order;
  const [f, setF] = useState(blank());
  const [touchedReceiver, setTouchedReceiver] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [newSup, setNewSup] = useState(null);   // 314: jméno pro kartu nového dodavatele
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tried, setTried] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF(order ? fromOrder(order) : { ...blank(pasport?.project_id || defaultProjectId), supplier_name: pasport?.supplier || "" });
    setNewSup(null);
    setTouchedReceiver(!!order); setTried(false); setBusy(false);
    api.get("/suppliers").then((r) => {
      const list = r.data || [];
      setSuppliers(list);
      // dodavatel podle vzorníku (pasport) — když ho seznam zná, rovnou vybraný
      if (!order && pasport?.supplier) {
        const hit = list.find((x) => x.name.toLowerCase() === pasport.supplier.toLowerCase());
        if (hit) setF((x) => ({ ...x, supplier_id: hit.id, supplier_name: hit.name }));
      }
    }).catch(() => setSuppliers([]));
    api.get("/projects/options").then((r) => setProjects((r.data || []).filter((p) => !CLOSED.includes(p.status)
      && (user?.role !== "MANAGER" || p.site_manager_id === user.id || (p.team || []).includes(user.id))))).catch(() => setProjects([]));
    api.get("/team").then((r) => setTeam((r.data || []).filter((m) => m.status !== "inactive" && RECEIVERS.includes(m.role)))).catch(() => setTeam([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kdo převezme: stavbyvedoucí zakázky, dokud ho člověk sám nezmění.
  useEffect(() => {
    if (touchedReceiver || !f.project_id || f.project_id === OFFICE) return;
    const p = projects.find((x) => x.id === f.project_id);
    if (p?.site_manager_id && team.some((m) => m.id === p.site_manager_id)) set("receiver_id", p.site_manager_id);
  }, [f.project_id, projects, team]); // eslint-disable-line react-hooks/exhaustive-deps

  const sup = useMemo(() => suppliers.find((s) => (f.supplier_id ? s.id === f.supplier_id : s.name.toLowerCase() === f.supplier_name.trim().toLowerCase())),
    [suppliers, f.supplier_id, f.supplier_name]);
  const split = splitAmount(f.amount, f.amount_mode, f.vat_rate);
  const lines = pasport?.items || [];
  const needProject = user?.role === "MANAGER";
  const missing = !f.supplier_name.trim() || (!lines.length && !f.title.trim()) || (needProject && (!f.project_id || f.project_id === OFFICE)) || !f.project_id;

  const pickFile = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    if (list.some((x) => !OK_FILE.test(x.name || ""))) { toast.error("Přiložit jde PDF nebo fotku."); return; }
    setUploading(true);
    try {
      for (const file of list) {
        const url = await uploadFile(file);
        setF((x) => ({ ...x, files: [...x.files, { file_url: url, name: file.name }] }));
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (missing) { setTried(true); return; }
    const body = {
      ...(sup ? { supplier_id: sup.id } : { supplier_name: f.supplier_name.trim() }), title: f.title.trim() || undefined, order_no: f.order_no,
      project_id: f.project_id === OFFICE ? null : f.project_id,
      amount: f.amount === "" ? null : f.amount, amount_mode: f.amount_mode, vat_rate: Number(f.vat_rate),
      due_date: f.due_date || null, due_note: f.due_note,
      delivery: { mode: f.mode, carrier: f.mode === "pickup" ? null : f.carrier, branch: f.mode === "pickup" ? f.branch : null },
      receiver_id: f.receiver_id || null, note: f.note,
    };
    if (f.files.length) body.files = f.files;
    if (!edit && lines.length) body.pasport_item_ids = lines.map((x) => x.id);
    if (edit && (order.lines || []).some((l) => l.pasport_item_id)) delete body.project_id;
    setBusy(true);
    try {
      const r = edit ? await api.patch(`/orders/${order.id}`, body) : await api.post("/orders", body);
      if (r.data.skipped?.length) toast.warning(`Přeskočeno ${r.data.skipped.length}: ${r.data.skipped.map((s) => `${s.label} (${s.why})`).join(", ")}`);
      toast.success(edit ? "Objednávka uložena." : `Objednáno: ${r.data.supplier_name}`);
      onSaved?.(r.data);
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const projLocked = edit ? (order.lines || []).some((l) => l.pasport_item_id) : !!pasport;
  const receiverName = team.find((m) => m.id === f.receiver_id)?.name;
  const err = (bad) => (tried && bad ? "border-destructive" : "");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent sheet className="w-[640px] max-w-[calc(100vw-2rem)] max-h-[92dvh] overflow-y-auto rounded-[22px]" data-testid="order-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-[22px] font-black tracking-tight">{edit ? "Upravit objednávku" : "Nová objednávka"}</DialogTitle>
          <DialogDescription>
            {lines.length ? [`${lines.length} ${lines.length === 1 ? "položka" : lines.length <= 4 ? "položky" : "položek"} z pasportu`, pasport.project_name].filter(Boolean).join(" · ")
              : "Zapište, co se u koho objednalo. Stavba dostane upozornění a převezme s fotkou."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {lines.length > 0 && (
            <ul className="rounded-[14px] border border-border divide-y divide-border/70 max-h-[160px] overflow-y-auto text-[13.5px]" data-testid="order-dialog-lines">
              {lines.map((x) => (
                <li key={x.id} className="flex items-center gap-2 px-3 py-2">
                  <span className="text-muted-foreground shrink-0">{x.room}</span>
                  <span className="flex-1 min-w-0 truncate font-semibold">{x.name}</span>
                  <span className="tabular-nums text-muted-foreground shrink-0">{String(x.qty ?? 1).replace(".", ",")} ks</span>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Dodavatel" req>
              <SupplierPicker suppliers={suppliers} name={f.supplier_name} supplierId={f.supplier_id} invalid={tried && !f.supplier_name.trim()}
                onType={(v) => setF((x) => ({ ...x, supplier_name: v, supplier_id: "" }))}
                onPick={(s) => setF((x) => ({ ...x, supplier_name: s.name, supplier_id: s.id }))}
                onNew={(name) => setNewSup(name)} />
            </Field>
            <Field label="Zakázka" req>
              <Select value={f.project_id || undefined} onValueChange={(v) => set("project_id", v)} disabled={projLocked}>
                <SelectTrigger className={`h-11 w-full min-w-0 ${err(!f.project_id || (needProject && f.project_id === OFFICE))}`} data-testid="order-project"><SelectValue placeholder="Vyberte zakázku" /></SelectTrigger>
                <SelectContent>
                  {!needProject && <SelectItem value={OFFICE}>Kancelář (bez zakázky)</SelectItem>}
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  {projLocked && pasport && !projects.some((p) => p.id === f.project_id) && <SelectItem value={f.project_id}>{pasport.project_name}</SelectItem>}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!lines.length && (
              <Field label="Co se objednává" req>
                <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="např. Obklady + dlažba"
                  data-testid="order-title" className={`h-11 w-full min-w-0 text-base md:text-sm ${err(!f.title.trim())}`} />
              </Field>
            )}
            <Field label="Číslo objednávky">
              <Input value={f.order_no} onChange={(e) => set("order_no", e.target.value)} placeholder="z potvrzení od dodavatele"
                data-testid="order-no" className="h-11 w-full min-w-0 text-base md:text-sm" />
            </Field>
          </div>

          <Field label="Částka">
              <div className="flex items-center gap-2 flex-wrap">
                <Input value={f.amount} onChange={(e) => set("amount", e.target.value)} inputMode="decimal" placeholder="0"
                  data-testid="order-amount" className="h-11 flex-1 min-w-[120px] md:max-w-[220px] text-base md:text-sm tabular-nums" />
                <div ref={autoPill} className="inline-flex h-11 p-1 rounded-full bg-secondary shrink-0" role="radiogroup" aria-label="Částka je">
                  {[["gross", "s DPH"], ["net", "bez DPH"]].map(([v, t]) => (
                    <button key={v} type="button" role="radio" aria-checked={f.amount_mode === v} onClick={() => set("amount_mode", v)} data-testid={`order-amount-${v}`}
                      className={`px-3 rounded-full text-[13px] font-bold whitespace-nowrap ${f.amount_mode === v ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{t}</button>
                  ))}
                </div>
                <select value={f.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} aria-label="Sazba DPH" data-testid="order-vat"
                  className="h-11 rounded-full bg-secondary px-3 text-[13px] font-semibold text-foreground shrink-0">
                  {[21, 12, 0].map((r) => <option key={r} value={r}>DPH {r} %</option>)}
                </select>
              </div>
              {split.gross != null && <p className="mt-1 text-[12.5px] text-muted-foreground tabular-nums" data-testid="order-amount-other">
                = {f.amount_mode === "gross" ? `${formatCZK(split.net)} bez DPH` : `${formatCZK(split.gross)} s DPH`} · cena pro stavbu je s DPH, náklad firmy bez DPH</p>}
          </Field>

          <Field label="Kam to přijde">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Kam to přijde">
              {Object.entries(MODES).map(([k, m]) => {
                const Icon = MODE_ICON[k];
                const on = f.mode === k;
                return (
                  <button key={k} type="button" role="radio" aria-checked={on} onClick={() => set("mode", k)} data-testid={`order-mode-${k}`}
                    className={`h-12 px-3 rounded-[14px] border inline-flex items-center gap-2 text-[14px] font-semibold text-left transition-colors ${on ? "border-brand bg-brand/10 pp-ink" : "border-border bg-card hover:bg-secondary"}`}>
                    <Icon size={17} className="shrink-0" />{m.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2">
              {f.mode === "pickup" ? (
                <>
                  <Input value={f.branch} onChange={(e) => set("branch", e.target.value)} placeholder="Pobočka, např. SIKO Černý Most"
                    data-testid="order-branch" className="h-11 w-full min-w-0 text-base md:text-sm" />
                  {(sup?.branches || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {sup.branches.map((b) => (
                        <button key={b.id} type="button" onClick={() => set("branch", b.name)}
                          className={`h-8 px-3 rounded-full text-[12.5px] font-semibold border ${f.branch === b.name ? "border-brand text-brand bg-brand/10" : "border-border bg-card"}`}>{b.name}</button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Input value={f.carrier} onChange={(e) => set("carrier", e.target.value)} placeholder="Dopravce, např. PPL, DHL, doprava SIKO"
                  data-testid="order-carrier" className="h-11 w-full min-w-0 text-base md:text-sm" />
              )}
            </div>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={f.mode === "pickup" ? "Připraveno od" : "Termín dodání"}>
              <div className="flex items-center gap-2">
                <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="order-due"
                  className="h-11 flex-1 w-full min-w-0 text-base md:text-sm" />
                <Input value={f.due_note} onChange={(e) => set("due_note", e.target.value)} placeholder="8–12 h"
                  aria-label="Upřesnění termínu" data-testid="order-due-note" className="h-11 w-[96px] min-w-0 text-base md:text-sm" />
              </div>
              {!f.due_date && <p className="text-[12px] text-muted-foreground mt-1">Termín zatím nevím — po 3 dnech připomeneme.</p>}
            </Field>
            <Field label={f.mode === "pickup" ? "Kdo vyzvedne" : "Kdo převezme"}>
              <Select value={f.receiver_id || "none"} onValueChange={(v) => { setTouchedReceiver(true); set("receiver_id", v === "none" ? "" : v); }}>
                <SelectTrigger className="h-11 w-full min-w-0" data-testid="order-receiver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Zatím nikdo</SelectItem>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Potvrzení objednávky (PDF nebo foto)">
            {(f.files.length > 0 || (edit && (order.files || []).length > 0)) && (
              <ul className="space-y-1.5 mb-2">
                {(edit ? order.files || [] : []).map((x) => (
                  <li key={x.id} className="flex items-center gap-2 text-[13.5px] text-muted-foreground"><FileText size={15} className="shrink-0" /><span className="truncate">{x.name}</span></li>
                ))}
                {f.files.map((x, i) => (
                  <li key={x.file_url} className="flex items-center gap-2 rounded-[12px] bg-success/10 border border-success/30 px-3 py-2 text-[13.5px]">
                    <FileText size={15} className="text-success shrink-0" /><span className="flex-1 min-w-0 truncate font-semibold">{x.name}</span>
                    <button type="button" onClick={() => set("files", f.files.filter((_, j) => j !== i))} aria-label="Odebrat přílohu"
                      className="text-muted-foreground hover:text-destructive"><X size={16} /></button>
                  </li>
                ))}
              </ul>
            )}
            <label className={`flex items-center justify-center gap-2 h-11 rounded-[12px] border border-dashed border-border cursor-pointer text-sm font-semibold text-muted-foreground hover:bg-secondary ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="order-file-btn">
              <Upload size={16} /> {uploading ? "Nahrávám…" : "Přiložit soubor"}
              <input type="file" multiple accept=".pdf,application/pdf,image/*" className="hidden" onChange={pickFile} data-testid="order-file-input" />
            </label>
          </Field>

          <Field label="Poznámka">
            <Textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} data-testid="order-note" className="text-base md:text-sm" />
          </Field>

          {(receiverName || lines.length > 0) && (
            <p className="text-[12.5px] text-muted-foreground">
              {receiverName && `${receiverName} dostane upozornění večer před termínem. `}
              {lines.length > 0 && "Položky v pasportu budou „Objednáno“ a stav se do nich bude propisovat."}
            </p>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse md:flex-row md:justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="w-full md:w-auto h-12 md:h-11 font-bold">Zrušit</Button>
          <Button onClick={save} disabled={busy || uploading} className="w-full md:w-auto h-12 md:h-11 font-bold px-6" data-testid="order-save">
            {busy ? "Ukládám…" : edit ? "Uložit změny" : "Uložit objednávku"}
          </Button>
        </DialogFooter>
        <SupplierEditDialog open={newSup !== null} onOpenChange={(o) => { if (!o) setNewSup(null); }} prefill={newSup || ""}
          onSaved={(s) => { setSuppliers((l) => [...l, s].sort((a, b) => a.name.localeCompare(b.name, "cs"))); setF((x) => ({ ...x, supplier_name: s.name, supplier_id: s.id })); }} />
      </DialogContent>
    </Dialog>
  );
}
